/**
 * anlage-ballsets — der Reiter „Historie": jeder Lauf dieses Tools, neueste zuerst.
 *
 * Das Protokoll ist die einzige Quelle des Doppelanlage-Schutzes (EING-05). Diese Tabelle
 * zeigt es unveraendert an: was gelaufen ist, was dabei entstand, wie viele der geplanten
 * Schreibvorgaenge wirklich stattgefunden haben und wer den Lauf gestartet hat.
 *
 * Drei Zustaende, die alle drei normal sind und die Seite nicht kaputtmachen duerfen:
 *   1. Laeufe da    → Tabelle, Klick auf eine Zeile klappt die Schritte inline auf.
 *   2. keine Laeufe → „Noch kein Lauf." Das Tool ist neu, das ist keine Stoerung.
 *   3. `hinweis`    → die Protokoll-Tabelle fehlt noch (Migration laeuft erst nach dem
 *      Merge durch den Admin). Der Hinweis steht UEBER der Tabelle und ersetzt sie nicht:
 *      er ist eine Betriebsinfo, kein Fehler — rot waere hier gelogen.
 *
 * Die Schritte eines Laufs kommen erst auf Klick nach (`GET /historie/:id`): in der Liste
 * stecken sie nicht, und alle auf Vorrat zu laden hiesse, fuer jeden Lauf eine Anfrage zu
 * stellen, die meistens niemand sehen will. Alle Pfade OHNE `/api/`-Prefix — der
 * Core-api-Client haengt ihn selbst an.
 *
 * Seit 06-07 steht im aufgeklappten Detail der Knopf „Erneut prüfen" (grau/gelbe Box des
 * Nachkontroll-Blocks). Er ruft `POST /nachkontrolle/:laufId` und laedt danach den Lauf neu —
 * ohne Job-Hook, siehe `pruefeErneut`.
 */
import { Fragment, useState } from 'react';
import { ChevronDown, ChevronRight, RefreshCw } from 'lucide-react';
import { api } from '@libero/core/services/api';
import { zahl, zeitpunkt } from '../formate';
import NachkontrolleBlock from './NachkontrolleBlock';
import type {
  HistorieAntwort,
  HistorieEintrag,
  LaufStatus,
  NachkontrolleStatus,
} from '../typen';

const PANEL = 'border border-[#DAD9D9] rounded-lg bg-white overflow-hidden';

/**
 * Farbe des Status-Badges. Unbekanntes bleibt grau, statt bunt zu raten.
 *
 * Exportiert, weil `ErgebnisBlock.tsx` dieselbe Schrittliste zeigt — nur direkt nach dem
 * Lauf statt spaeter aus dem Protokoll. Zwei Farbschemata fuer dieselben Status waeren ein
 * Fehler, der erst auffaellt, wenn jemand beide Ansichten nebeneinander legt.
 */
export function badgeFarbe(status: LaufStatus | string): string {
  if (status === 'fertig') return 'bg-green-100 text-green-900';
  if (status === 'laeuft') return 'bg-yellow-100 text-yellow-900';
  // `teilweise` traegt dieselbe Gelbstufe wie `laeuft`: beides heisst „nicht fertig, aber
  // kein Fehlschlag". Rot waere hier gelogen — der Artikel STEHT in Plenty, es fehlen nur
  // Kopier-Schritte. Diese eine Zeile faerbt beide Ansichten, weil `ErgebnisBlock.tsx`
  // dieselbe Funktion aufruft; ein zweites Schema dort waere der Fehler, vor dem der
  // Kopfkommentar warnt.
  if (status === 'teilweise') return 'bg-yellow-100 text-yellow-900';
  if (status === 'fehlgeschlagen') return 'bg-red-100 text-red-900';
  return 'bg-gray-100 text-gray-700';
}

/**
 * Farbe des Nachkontroll-Badges — die ZWEITE Spalte neben dem Lauf-Status, nicht sein
 * Ersatz: ein `fertig` beendeter Lauf kann Abweichungen haben, und ein `teilweise` beendeter
 * kann sauber sein.
 *
 * Exportiert aus demselben Grund wie `badgeFarbe`: `NachkontrolleBlock.tsx` und diese Tabelle
 * zeigen dieselben drei Status, und zwei Farbschemata dafuer waeren ein Fehler, der erst
 * auffaellt, wenn jemand beide Ansichten nebeneinander legt.
 *
 * `null`/`undefined` ist der NORMALFALL fuer jeden Lauf vor dieser Phase und fuer jeden Lauf
 * ohne angelegten Artikel. Er bekommt blasses Grau und einen Strich — geraten wird nichts.
 */
export function nachkontrolleBadgeFarbe(
  status: NachkontrolleStatus | string | null | undefined,
): string {
  if (status === 'ok') return 'bg-green-100 text-green-900';
  if (status === 'abweichungen') return 'bg-yellow-100 text-yellow-900';
  if (status === 'nicht_moeglich') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-400';
}

/** Farbe eines einzelnen Schritts — dieselbe Logik, andere Worte (Plenty-Ebene). */
export function schrittFarbe(status: string): string {
  if (status === 'ok') return 'text-green-800';
  if (status === 'fehler') return 'text-red-800';
  if (status === 'uebersprungen') return 'text-gray-500';
  return 'text-gray-600';
}

export default function Historie({
  antwort,
  laedt,
  onNeuLaden,
}: {
  antwort: HistorieAntwort | null;
  laedt?: boolean;
  onNeuLaden: () => void;
}) {
  const [offenerLauf, setOffenerLauf] = useState<number | null>(null);
  const [detail, setDetail] = useState<Record<number, HistorieEintrag>>({});
  const [detailFehler, setDetailFehler] = useState<string | null>(null);
  /** Die Lauf-Nummer, die gerade nachgeprueft wird — sperrt genau ihre Zeile, keine andere. */
  const [prueftLauf, setPrueftLauf] = useState<number | null>(null);

  const eintraege = antwort?.eintraege ?? [];

  /** Ein Lauf-Detail (neu) holen — dieselbe Anfrage wie beim Aufklappen. */
  const ladeDetail = async (id: number) => {
    const res = await api.get(`/programmes/anlage-ballsets/historie/${id}`);
    setDetail((bisher) => ({ ...bisher, [id]: res.data.data }));
  };

  /**
   * „Erneut prüfen" in der Detailansicht.
   *
   * HIER STEHT BEWUSST KEIN `useJobStatus`: die Historie kennt keinen laufenden Job und
   * braucht keinen Fortschritt — sie braucht den aktualisierten Datensatz. Der Bericht steht
   * am LAUF, nicht am Job; nach der Antwort wird deshalb schlicht `GET /historie/:id` neu
   * geladen (und die Liste, damit auch das Badge in der Tabelle stimmt).
   *
   * Antwortet die Route mit 409 (`JOB_ALREADY_RUNNING` — eine andere Nachkontrolle laeuft),
   * wird der Text des Servers angezeigt; einen eigenen Statuscode-Zweig gibt es nicht.
   * Alle Pfade OHNE `/api/`-Prefix.
   */
  const pruefeErneut = async (id: number) => {
    if (prueftLauf !== null) return;
    setPrueftLauf(id);
    setDetailFehler(null);
    try {
      await api.post(`/programmes/anlage-ballsets/nachkontrolle/${id}`);
      await ladeDetail(id);
      onNeuLaden();
    } catch (err: any) {
      setDetailFehler(err.response?.data?.error?.message ?? err.message);
    } finally {
      setPrueftLauf(null);
    }
  };

  const oeffne = async (lauf: HistorieEintrag) => {
    if (offenerLauf === lauf.id) {
      setOffenerLauf(null);
      return;
    }
    setOffenerLauf(lauf.id);
    setDetailFehler(null);
    if (detail[lauf.id]) return;
    try {
      await ladeDetail(lauf.id);
    } catch (err: any) {
      setDetailFehler(err.response?.data?.error?.message ?? err.message);
    }
  };

  return (
    <div className={PANEL}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#DAD9D9]">
        <h2 className="text-sm font-bold text-[#1A1A1A]">Läufe</h2>
        <button
          type="button"
          onClick={onNeuLaden}
          disabled={laedt}
          title="Historie neu laden"
          className="text-gray-500 hover:text-[#1A1A1A] disabled:opacity-50"
        >
          <RefreshCw size={13} />
        </button>
      </div>

      {antwort?.hinweis && (
        <p className="px-4 pt-3 text-xs text-gray-500 italic">{antwort.hinweis}</p>
      )}

      {eintraege.length === 0 ? (
        <p className="p-4 text-xs text-gray-500 italic">Noch kein Lauf.</p>
      ) : (
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A] text-white">
            <tr>
              <th className="px-3 py-1.5 text-left font-medium">Wann</th>
              <th className="px-3 py-1.5 text-left font-medium">Original</th>
              <th className="px-3 py-1.5 text-left font-medium">Setgrößen</th>
              <th className="px-3 py-1.5 text-left font-medium">Status</th>
              <th className="px-3 py-1.5 text-left font-medium">Nachkontrolle</th>
              <th className="px-3 py-1.5 text-left font-medium">Ballset</th>
              <th className="px-3 py-1.5 text-right font-medium">Schreibvorgänge</th>
              <th className="px-3 py-1.5 text-left font-medium">gestartet von</th>
            </tr>
          </thead>
          <tbody>
            {eintraege.map((l) => (
              // Das Listenelement ist das Fragment (Zeile + aufgeklappte Schritte) — der
              // `key` gehoert deshalb dorthin und nicht an das erste `<tr>`.
              <Fragment key={l.id}>
                <tr
                  onClick={() => void oeffne(l)}
                  className="border-b border-[#DAD9D9] last:border-0 cursor-pointer hover:bg-gray-50"
                >
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {offenerLauf === l.id
                      ? <ChevronDown size={11} className="inline mr-1" />
                      : <ChevronRight size={11} className="inline mr-1" />}
                    {zeitpunkt(l.createdAt)}
                    <span className="text-gray-400"> #{l.id}</span>
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {l.variantenNr}
                    <span className="text-gray-400"> (Item {l.originalItemId})</span>
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {(l.setgroessen ?? []).join(' + ') || '—'}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${badgeFarbe(l.status)}`}>
                      {l.status}
                    </span>
                  </td>
                  <td className="px-3 py-1.5">
                    {/*
                      Ohne Nachkontrolle steht hier schlicht ein grauer Strich und KEIN Badge:
                      ein Lauf aus der Zeit vor dieser Phase hat nichts zu melden, und ein
                      Badge mit „—" behauptete ein Ergebnis, das es nicht gibt.
                    */}
                    {l.nachkontrolleStatus ? (
                      <span
                        className={`px-1.5 py-0.5 rounded text-[10px] ${nachkontrolleBadgeFarbe(l.nachkontrolleStatus)}`}
                      >
                        {l.nachkontrolleStatus}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    {l.ballsetItemId ? `#${l.ballsetItemId}` : '—'}
                  </td>
                  <td className="px-3 py-1.5 text-right whitespace-nowrap">
                    {zahl(l.writesAusgefuehrt ?? 0)}
                    <span className="text-gray-400"> / {zahl(l.writesGeplant ?? 0)}</span>
                  </td>
                  <td className="px-3 py-1.5 text-gray-600">{l.gestartetVon ?? '—'}</td>
                </tr>

                {offenerLauf === l.id && (
                  <tr className="border-b border-[#DAD9D9]">
                    <td colSpan={8} className="px-4 py-2 bg-gray-50">
                      {l.fehler && (
                        <p className="mb-2 text-[11px] text-red-800">{l.fehler}</p>
                      )}
                      {detailFehler && (
                        <p className="text-[11px] text-red-800">{detailFehler}</p>
                      )}
                      {!detail[l.id] && !detailFehler && (
                        <p className="text-[11px] text-gray-500 italic">Lädt …</p>
                      )}
                      {detail[l.id] && (detail[l.id].schritte ?? []).length === 0 && (
                        <p className="text-[11px] text-gray-500 italic">
                          Keine Schritte protokolliert.
                        </p>
                      )}
                      {(detail[l.id]?.schritte ?? []).map((s) => (
                        <div
                          key={s.id}
                          className="flex items-baseline gap-2 py-0.5 text-[11px]"
                        >
                          <span className={`w-24 shrink-0 ${schrittFarbe(s.status)}`}>
                            {s.status}
                          </span>
                          <span className="w-28 shrink-0 text-gray-600">{s.art}</span>
                          <span className="font-mono text-gray-700 break-all">{s.pfad}</span>
                          {s.plentyId != null && (
                            <span className="text-gray-500">→ {s.plentyId}</span>
                          )}
                          {s.fehler && <span className="text-red-800">{s.fehler}</span>}
                        </div>
                      ))}

                      {/*
                        Derselbe Block wie im Ergebnisblock — eine Komponente, zweimal
                        verwendet. Die Lade-Semantik ist die der Schritte: der Bericht kommt
                        erst mit `GET /historie/:id` mit, in der Liste steht nur sein Status.
                        Bis dahin (und bei jedem Lauf ohne Bericht) zeigt der Block nichts.
                      */}
                      {detail[l.id] && (
                        <div className="mt-2">
                          <NachkontrolleBlock
                            nachkontrolle={detail[l.id]?.nachkontrolle ?? null}
                            onErneutPruefen={() => { void pruefeErneut(l.id); }}
                            laeuft={prueftLauf === l.id}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
